/**
 * Presentation vocabulary shared by the Candidates and Engaged pages.
 *
 * Both render the same membership rows, so a label that lives in only one of
 * them is a label the two pages will eventually disagree about.
 */

export const STAGE_LABELS: Record<string, string> = {
  staged: 'Staged',
  qualified: 'Qualified',
  active: 'Active',
  engaged: 'Engaged',
  timed_out: 'Timed Out',
  disqualified: 'Disqualified',
}

// Strategy plays keep people or find them not a fit; classic plays keep their own words.

const REASON_LABELS: Record<string, string> = {
  manual_user_add: 'Added manually',
  csv_upload: 'CSV upload',
  chat_direct_add: 'Added via chat',
  agent_include: 'Included by agent',
  flock_kept: 'Kept',
  flock_excluded: 'Not a fit',
  agent_exclude: 'Excluded by agent',
  agent_decision: 'Agent decision',
  source_produced: 'Added from source',
  agent_prospecting_add: 'Found by agent',
  agent_prospecting: 'Found by agent',
  sequence: 'Added to sequence',
  dialer: 'Added to dialer queue',
  approved: 'Approved',
  requalified: 'Requalified',
  replied_positive: 'Replied positively',
  replied_not_interested: 'Not interested',
  no_response: 'No response',
  bad_icp_fit: 'Bad ICP fit',
  bad_persona_fit: 'Bad persona fit',
  engaged: 'Engaged',
  bounced: 'Email bounced',
  unsubscribed: 'Unsubscribed',
  user_decision: 'Manually removed',
  disqualified: 'Disqualified',
  icp: 'Does not match ICP',
  persona: 'Does not match persona',
  already_customer: 'Already a customer',
  wrong_contact: 'Wrong contact',
  account_disqualified: 'Account disqualified',
  ownership_blocked: 'Ownership blocked',
  bulk_exit: 'Exited play',
  reset_to_staged: 'Reset to staged',
  filter: 'Failed filter',
  contact_moved_account: 'Moved company',
  champion_job_change: 'Champion job change',
}

export function reasonLabel(reason: string | null | undefined): string {
  if (!reason) return '—'
  if (REASON_LABELS[reason]) return REASON_LABELS[reason]
  // Block types arrive here verbatim (sfdc_contact_search); a new block should read
  // correctly without an entry above.
  return reason.replace(/[_-]+/g, ' ').replace(/\b(\w)/g, (m) => m.toUpperCase())
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const mins = Math.floor((Date.now() - d.getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

/** Absolute timestamp for the title attribute behind a relative one. */
export function absoluteTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString()
}

// ── Activity pills ───────────────────────────────────────────────

export type ActivityPill = {
  type: string
  id: string
  account_id?: string
  occurred_at?: string | null
  direction?: 'in' | 'out' | null
  label?: string
  status?: string
  /** Which CRM a deal came from, so the right detail dialog opens. */
  source?: string
  data?: Record<string, any> | null
}

type PillPresentation = { icon: string; kind: string; color?: string }

/**
 * Icon + colour per activity type, matching the drawer's timeline so the same
 * activity reads the same on both surfaces. Direction matters more than channel
 * for email: inbound is the thing a rep is looking for, so it takes the blue
 * while outbound touches stay muted.
 */
export function activityPillPresentation(pill: ActivityPill): PillPresentation {
  const type = pill.type
  // A Gmail signal is either an inbound reply or an ad-hoc outbound email;
  // direction is what distinguishes them, so it drives both label and colour.
  if (type === 'signal') {
    return pill.direction === 'out'
      ? { icon: 'email', kind: 'Email sent', color: '#94a3b8' }
      : { icon: 'email', kind: 'Reply received', color: '#3b82f6' }
  }
  if (type === 'reachout_email') return { icon: 'email', kind: 'Email sent', color: '#94a3b8' }
  if (type === 'reachout_voicemail') return { icon: 'voicemail', kind: 'Voicemail' }
  if (type === 'reachout_call') return { icon: 'phone_call', kind: 'Call' }
  if (type === 'reachout_linkedin_connect') return { icon: 'reachout', kind: 'LinkedIn' }
  if (type === 'call_log') return { icon: 'phone_call', kind: 'Call', color: 'var(--color-scheduled, #0ea5e9)' }
  if (type === 'gong_call') return { icon: 'phone_call', kind: 'Gong call' }
  if (type === 'salesforce_event' || type === 'hubspot_meeting' || type === 'pipedrive_meeting') {
    return { icon: 'calendar', kind: 'Meeting', color: 'var(--color-accent, #4f46e5)' }
  }
  if (type === 'opportunity') {
    return { icon: 'opportunity', kind: 'Opportunity', color: 'var(--color-warning, #ca8a04)' }
  }
  if (type === 'salesforce_custom_object') {
    return { icon: 'timeline', kind: 'Salesforce record' }
  }
  return { icon: 'timeline', kind: 'Activity' }
}

/** Tooltip text: what it is, when, and its subject where there is one. */
export function activityPillTitle(pill: ActivityPill): string {
  const { kind } = activityPillPresentation(pill)
  const parts = [kind]
  if (pill.label) parts.push(pill.label)
  const when = absoluteTime(pill.occurred_at)
  if (when) parts.push(when)
  return parts.join(' — ')
}
